/**
 * Value-free apart from types on purpose: `src/testids/sources/tpogen.ts` imports
 * the scope options from here to publish their locators, and that generator must
 * not pull the Mixpanel client in with them. Anything needing `trackTpoGenEvent`
 * belongs in `utils/exportAnalytics.ts`.
 */
import type { ExportScope } from '../types';

/**
 * Polling taper, per section 6 of the FE/BE contract. Tiers are read in order
 * and the first whose `untilMs` the elapsed time has not passed wins.
 */
export const POLL_TIERS: ReadonlyArray<{ untilMs: number; intervalMs: number }> = [
    { untilMs: 10_000, intervalMs: 2_000 },
    { untilMs: 60_000, intervalMs: 5_000 },
    { untilMs: 300_000, intervalMs: 10_000 },
];

/**
 * Hard ceiling. Past this the FE stops polling and drops the job whether or not
 * the backend reached a terminal state, so a stuck job cannot poll forever.
 */
export const POLL_CEILING_MS = 300_000;

/** Versioned per the storage rules; a shape change bumps the prefix. */
export const EXPORT_JOBS_STORAGE_KEY = 'v1:tpoGen:reportExportJobs';

/** The only format the backend produces today; reported as-is to analytics. */
export const EXPORT_FORMAT = 'xlsx';

export const EXPORT_FALLBACK_FILENAME = 'report_export.xlsx';

export const EXPORT_MODAL_LABELS = {
    title: 'Export Report',
    subtitle: 'Choose what to include in the exported file.',
    currentView: 'Export current view',
    currentViewHint: 'Applies the filters, search, sorting and KPI selection currently on screen.',
    allData: 'Export all data',
    allDataHint: 'Ignores filters and search, and includes every KPI in the catalog.',
    confirm: 'Export',
    cancel: 'Cancel',
} as const;

/**
 * The modal's two radios, in display order. `value` is the locator key, so it
 * is the `ExportScope` itself rather than the translated label.
 */
export const EXPORT_SCOPE_OPTIONS: ReadonlyArray<{ value: ExportScope; label: string; hint: string }> = [
    {
        value: 'current_view',
        label: EXPORT_MODAL_LABELS.currentView,
        hint: EXPORT_MODAL_LABELS.currentViewHint,
    },
    { value: 'all_data', label: EXPORT_MODAL_LABELS.allData, hint: EXPORT_MODAL_LABELS.allDataHint },
];

export const EXPORT_NOTIFICATIONS = {
    queued: 'Export started. The file will download automatically once it is ready.',
    completed: 'Export ready. Your download has started.',
    createFailed: 'Could not start the export. Please try again.',
    failed: 'Export failed. Please try again.',
    timedOut: 'Export is taking longer than expected and was stopped. Please try again.',
    downloadFailed: 'The export finished but the file could not be downloaded.',
} as const;
